/**
 * Pre-Toss Squad Selection
 * The scorer picks the Playing Squad (size = event.playing_xi) + Captain + Vice-Captain + Wicketkeeper
 * for both teams before the toss can happen.
 *
 * Rulebook selection rules (phase 5): "Rules for this squad" per team, a batting
 * order to tap in (who bats 1, 2, 3… — needed when the top order can't bowl),
 * and "Lock anyway" with a reason for organisers when a blocking rule fails.
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';

export default function SquadSelectionPanel({ eventId, fixtureId, onLocked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // working selections keyed by team_id: { [team_id]: Map(player_pool_id -> {is_captain, is_vice_captain, is_wicketkeeper}) }
  const [selections, setSelections] = useState({});
  const [checks, setChecks] = useState({});        // team_id -> "Rules for this squad"
  const [blocked, setBlocked] = useState({});      // team_id -> {items, canOverride} after a refused lock
  const [reasons, setReasons] = useState({});      // team_id -> organiser's reason to lock anyway

  const loadChecks = useCallback(async (teams) => {
    const out = {};
    await Promise.all((teams || []).map(async (t) => {
      try {
        const r = await axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/squads/${t.team_id}/rules`);
        if (r.data?.items?.length) out[t.team_id] = r.data;
      } catch { /* no rules shown */ }
    }));
    setChecks(out);
  }, [eventId, fixtureId]);

  const fetchSquads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/live/${fixtureId}/squads`);
      setData(res.data);
      // Initialise working selections:
      //   • If the server already has a saved squad for this team, use it.
      //   • Otherwise pre-select the full roster (scorer just unchecks anyone
      //     not playing) — faster than ticking 11 boxes from scratch.
      const initial = {};
      const rosterByTeam = res.data.roster_by_team || {};
      (res.data.teams || []).forEach(t => {
        initial[t.team_id] = {};
        const hasSavedSquad = Array.isArray(t.players) && t.players.length > 0;
        if (hasSavedSquad) {
          t.players.forEach(p => {
            initial[t.team_id][p.player_pool_id] = {
              is_captain: !!p.is_captain,
              is_vice_captain: !!p.is_vice_captain,
              is_wicketkeeper: !!p.is_wicketkeeper,
              tag: p.tag || null,
              batting_order: p.batting_order || null,
            };
          });
        } else {
          // Fresh selection — pre-tick every rostered player and auto-mark
          // the first player as Captain. Backend requires exactly one Captain
          // for save+lock; without a default the user has to remember to
          // assign one or the lock silently rejects.
          (rosterByTeam[t.team_id] || []).forEach((p, idx) => {
            initial[t.team_id][p.player_pool_id] = {
              is_captain: idx === 0,
              is_vice_captain: false,
              is_wicketkeeper: false,
            };
          });
        }
      });
      setSelections(initial);
      loadChecks(res.data.teams);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load squads');
    } finally {
      setLoading(false);
    }
  }, [eventId, fixtureId, loadChecks]);

  useEffect(() => { fetchSquads(); }, [fetchSquads]);

  // Batting order: tap to give the next number; tap again to take it off (the rest close up)
  const toggleOrder = (teamId, playerId) => {
    setSelections(prev => {
      const teamSel = { ...(prev[teamId] || {}) };
      const cur = teamSel[playerId];
      if (!cur) return prev;
      if (cur.batting_order) {
        const gone = cur.batting_order;
        Object.keys(teamSel).forEach(pid => {
          const o = teamSel[pid].batting_order;
          if (o && o > gone) teamSel[pid] = { ...teamSel[pid], batting_order: o - 1 };
        });
        teamSel[playerId] = { ...teamSel[playerId], batting_order: null };
      } else {
        const next = Math.max(0, ...Object.values(teamSel).map(v => v.batting_order || 0)) + 1;
        teamSel[playerId] = { ...cur, batting_order: next };
      }
      return { ...prev, [teamId]: teamSel };
    });
  };

  const togglePlayer = (teamId, playerId) => {
    setSelections(prev => {
      const teamSel = { ...(prev[teamId] || {}) };
      if (teamSel[playerId]) {
        delete teamSel[playerId];
      } else {
        teamSel[playerId] = { is_captain: false, is_vice_captain: false, is_wicketkeeper: false };
      }
      return { ...prev, [teamId]: teamSel };
    });
  };

  const setRole = (teamId, playerId, roleKey) => {
    // roles are exclusive (one C / one VC / one WK per team),
    // but C and VC can't be the same player; WK can overlap with C or VC.
    setSelections(prev => {
      const teamSel = { ...(prev[teamId] || {}) };
      if (!teamSel[playerId]) return prev; // must be selected first
      const currentlyOn = !!teamSel[playerId][roleKey];
      // If turning on C -> remove C flag from every other player, and if this player was VC, clear VC.
      // If turning on VC -> remove VC flag from every other player, and if this player was C, clear C.
      // If turning on WK -> remove WK flag from every other player (may coexist with C/VC).
      Object.keys(teamSel).forEach(pid => {
        if (pid === playerId) return;
        if (currentlyOn) return; // we are turning off; don't touch others
        teamSel[pid] = { ...teamSel[pid], [roleKey]: false };
      });
      const cur = teamSel[playerId];
      const nextVal = !currentlyOn;
      let updated = { ...cur, [roleKey]: nextVal };
      if (nextVal) {
        if (roleKey === 'is_captain') updated.is_vice_captain = false;
        if (roleKey === 'is_vice_captain') updated.is_captain = false;
      }
      teamSel[playerId] = updated;
      return { ...prev, [teamId]: teamSel };
    });
  };

  // Impact-player roles from the rulebook (e.g. "impact batsman"): one player each per team
  const setTag = (teamId, playerId, tag) => {
    setSelections(prev => {
      const teamSel = { ...(prev[teamId] || {}) };
      if (!teamSel[playerId]) return prev;
      const on = teamSel[playerId].tag === tag;
      Object.keys(teamSel).forEach(pid => {
        if (teamSel[pid].tag === tag) teamSel[pid] = { ...teamSel[pid], tag: null };
      });
      teamSel[playerId] = { ...teamSel[playerId], tag: on ? null : tag };
      return { ...prev, [teamId]: teamSel };
    });
  };
  // Short label for a role button: initials ("IB"), or the last word when two roles share initials ("Bats", "Bowl")
  const initials = (tag) => tag.split(/\s+/).map(w => w[0]?.toUpperCase()).join('').slice(0, 3);
  const tagInitials = (tag) => {
    const clash = (data?.tags || []).filter(t => initials(t.tag) === initials(tag)).length > 1;
    if (!clash) return initials(tag);
    const last = tag.trim().split(/\s+/).pop();
    return last.charAt(0).toUpperCase() + last.slice(1, 4);
  };

  const submitAndLock = async (teamId) => {
    const required = data?.required_size || 11;
    const minSize = data?.min_size || required;
    const team = (data?.teams || []).find(t => t.team_id === teamId);
    if (!team) return;
    const sel = selections[teamId] || {};
    const picked = Object.entries(sel).map(([player_pool_id, flags]) => ({
      player_pool_id,
      is_captain: !!flags.is_captain,
      is_vice_captain: !!flags.is_vice_captain,
      is_wicketkeeper: !!flags.is_wicketkeeper,
      tag: flags.tag || null,
      batting_order: flags.batting_order || null,
    }));

    if (picked.length < minSize || picked.length > required) {
      const msg = minSize === required
        ? `Select exactly ${required} players (currently ${picked.length})`
        : `Select between ${minSize} and ${required} players (currently ${picked.length})`;
      toast.error(msg);
      return;
    }
    if (picked.filter(p => p.is_captain).length !== 1) {
      toast.error('Select exactly one Captain');
      return;
    }
    if (picked.filter(p => p.is_wicketkeeper).length > 1) {
      toast.error('At most one Wicketkeeper allowed');
      return;
    }
    if (picked.filter(p => p.is_vice_captain).length > 1) {
      toast.error('At most one Vice-Captain allowed');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put(
        `/v1/events/${eventId}/live/${fixtureId}/squads/${teamId}`,
        { players: picked }
      );
      await lockSquad(teamId);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === 'squad_rules') {
        setBlocked(b => ({ ...b, [teamId]: { items: d.items, canOverride: d.can_override } }));
        loadChecks(data?.teams);
      } else {
        toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to save squad');
      }
    } finally {
      setSaving(false);
    }
  };

  const lockSquad = async (teamId, overrideReason) => {
    const team = (data?.teams || []).find(t => t.team_id === teamId);
    await axiosInstance.post(`/v1/events/${eventId}/live/${fixtureId}/squads/${teamId}/lock`,
      overrideReason ? { override_reason: overrideReason } : undefined);
    toast.success(`${team?.team_name || 'Team'} squad locked`);
    setBlocked(b => ({ ...b, [teamId]: null }));
    await fetchSquads();
    if (onLocked) onLocked();
  };

  const lockAnyway = async (teamId) => {
    setSaving(true);
    try {
      await lockSquad(teamId, (reasons[teamId] || '').trim());
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to lock');
    } finally {
      setSaving(false);
    }
  };

  const unlock = async (teamId) => {
    if (!window.confirm('Unlock this squad for editing?')) return;
    setSaving(true);
    try {
      await axiosInstance.delete(`/v1/events/${eventId}/live/${fixtureId}/squads/${teamId}/lock`);
      toast.success('Squad unlocked');
      await fetchSquads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }
  if (!data) return null;

  const required = data.required_size;
  const minSize = data.min_size || required;
  const sizeLabel = minSize === required ? `${required}` : `${minSize}–${required}`;

  return (
    <div className="space-y-6" data-testid="squad-selection-panel">
      <div className="bg-[#2B3A67] rounded-xl p-4 text-center">
        <h3 className="text-white font-bold text-lg">
          <i className="ph-bold ph-users-three mr-2"></i>
          Pre-Toss Squad Selection
        </h3>
        <p className="text-white/70 text-sm mt-1">
          Pick {sizeLabel} players per team. Assign a Captain. Vice-Captain & Wicketkeeper are optional.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data.teams || []).map(team => {
          const roster = data.roster_by_team?.[team.team_id] || [];
          const sel = selections[team.team_id] || {};
          const pickedCount = Object.keys(sel).length;
          const hasC = Object.values(sel).some(v => v.is_captain);
          const hasVC = Object.values(sel).some(v => v.is_vice_captain);
          const hasWK = Object.values(sel).some(v => v.is_wicketkeeper);
          const locked = team.is_locked;

          return (
            <div
              key={team.team_id}
              className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
              data-testid={`squad-team-${team.team_id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {team.team_logo_url && (
                    <img src={team.team_logo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                  )}
                  <h4 className="font-bold text-[#2B3A67]">{team.team_name}</h4>
                </div>
                <div
                  className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    locked ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}
                  data-testid={`squad-lock-status-${team.team_id}`}
                >
                  {locked ? 'LOCKED' : `${pickedCount}/${sizeLabel}`}
                </div>
              </div>

              {roster.length === 0 ? (
                <div className="text-xs text-gray-500 italic py-4 text-center">
                  No players assigned to this team yet.
                </div>
              ) : (
                <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
                  {roster.map(p => {
                    const picked = !!sel[p.player_pool_id];
                    const flags = sel[p.player_pool_id] || {};
                    const disabled = locked || saving;
                    return (
                      <div
                        key={p.player_pool_id}
                        className={`flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm ${
                          picked ? 'bg-[#3AAFA9]/10 border border-[#3AAFA9]/30' : 'bg-gray-50 border border-transparent'
                        }`}
                        data-testid={`roster-row-${p.player_pool_id}`}
                      >
                        <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={picked}
                            disabled={disabled || (!picked && pickedCount >= required)}
                            onChange={() => togglePlayer(team.team_id, p.player_pool_id)}
                            data-testid={`squad-pick-${p.player_pool_id}`}
                          />
                          {p.player_photo ? (
                            <img src={p.player_photo} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0">
                              {p.player_name?.[0] || '?'}
                            </span>
                          )}
                          <span className="truncate font-medium text-gray-800">{p.player_name}</span>
                          {p.category && checks[team.team_id] && p.playing_role !== p.category && (
                            <span className="text-[10px] font-bold px-1 rounded bg-[#E9EBF1] text-[#2B3A67]" title="Category">{p.category}</span>
                          )}
                          {p.playing_role && (
                            <span className="text-[10px] uppercase tracking-wide text-gray-500">
                              {p.playing_role}
                            </span>
                          )}
                        </label>
                        {picked && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {checks[team.team_id] && (
                              <button
                                onClick={() => toggleOrder(team.team_id, p.player_pool_id)}
                                disabled={disabled}
                                title="Batting order: tap in the order they bat"
                                className={`text-[10px] font-bold h-6 min-w-[24px] px-1 rounded-full ${
                                  flags.batting_order ? 'bg-[#2B3A67] text-white' : 'bg-white text-[#2B3A67] border border-[#2B3A67]/40'
                                }`}
                                data-testid={`bat-order-${p.player_pool_id}`}
                              >{flags.batting_order || 'Bat'}</button>
                            )}
                            <button
                              onClick={() => setRole(team.team_id, p.player_pool_id, 'is_captain')}
                              disabled={disabled}
                              title="Captain"
                              className={`text-[10px] font-bold w-6 h-6 rounded-full ${
                                flags.is_captain ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 border border-amber-300'
                              }`}
                              data-testid={`role-c-${p.player_pool_id}`}
                            >C</button>
                            <button
                              onClick={() => setRole(team.team_id, p.player_pool_id, 'is_vice_captain')}
                              disabled={disabled}
                              title="Vice-Captain"
                              className={`text-[10px] font-bold w-6 h-6 rounded-full ${
                                flags.is_vice_captain ? 'bg-indigo-500 text-white' : 'bg-white text-indigo-700 border border-indigo-300'
                              }`}
                              data-testid={`role-vc-${p.player_pool_id}`}
                            >VC</button>
                            <button
                              onClick={() => setRole(team.team_id, p.player_pool_id, 'is_wicketkeeper')}
                              disabled={disabled}
                              title="Wicketkeeper"
                              className={`text-[10px] font-bold w-6 h-6 rounded-full ${
                                flags.is_wicketkeeper ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 border border-emerald-300'
                              }`}
                              data-testid={`role-wk-${p.player_pool_id}`}
                            >WK</button>
                            {(data?.tags || []).map(t => (
                              <button
                                key={t.tag}
                                onClick={() => setTag(team.team_id, p.player_pool_id, t.tag)}
                                disabled={disabled}
                                title={t.tag}
                                className={`text-[10px] font-bold h-6 px-1.5 rounded-full ${
                                  flags.tag === t.tag ? 'bg-[#E54562] text-white' : 'bg-white text-[#B3263F] border border-[#E54562]/50'
                                }`}
                                data-testid={`role-tag-${tagInitials(t.tag)}-${p.player_pool_id}`}
                              >{tagInitials(t.tag)}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-3 text-[11px] text-gray-500">
                C: {hasC ? '✓' : '—'} &nbsp; VC: {hasVC ? '✓' : 'opt'} &nbsp; WK: {hasWK ? '✓' : 'opt'}
                {(data?.tags || []).map(t => (
                  <span key={t.tag} className="ml-2">
                    {tagInitials(t.tag)} = {t.tag}: {Object.values(sel).some(v => v.tag === t.tag) ? '✓' : '—'}
                  </span>
                ))}
              </div>

              {checks[team.team_id] && (
                <div className="mt-3 rounded-lg border border-gray-100 p-2" data-testid={`squad-rules-${team.team_id}`}>
                  <p className="text-[11px] font-bold text-gray-500 mb-1">Rules for this squad</p>
                  <ul className="space-y-1">
                    {checks[team.team_id].items.map(i => (
                      <li key={i.rule_key + i.label} className="text-xs flex gap-1.5">
                        <i className={`ph-bold mt-0.5 ${i.ok ? 'ph-check-circle text-[#1F7A75]' : i.blocking ? 'ph-x-circle text-[#B3263F]' : 'ph-warning text-[#2B3A67]'}`} />
                        <span><span className="font-semibold">{i.label}</span> — {i.text}{i.note ? ` (${i.note})` : ''}</span>
                      </li>
                    ))}
                  </ul>
                  {checks[team.team_id].overridden && <p className="text-[11px] text-gray-500 mt-1">Locked anyway by the organiser.</p>}
                  {!locked && checks[team.team_id].top_needed > 0 && (
                    <p className="text-[11px] text-gray-500 mt-1">Tap “Bat” in batting order; the top {checks[team.team_id].top_needed} can't bowl.</p>
                  )}
                </div>
              )}

              {blocked[team.team_id] && !locked && (
                <div className="mt-3 rounded-lg bg-[#FDF5D3] p-3 text-[#2B3A67]" data-testid={`squad-blocked-${team.team_id}`}>
                  <p className="text-sm font-bold">This squad breaks a rule</p>
                  <ul className="text-xs mt-1 list-disc pl-4">
                    {blocked[team.team_id].items.map(i => <li key={i.rule_key + i.label}>{i.label}: {i.text}</li>)}
                  </ul>
                  {blocked[team.team_id].canOverride ? (
                    <div className="mt-2 flex gap-2">
                      <input value={reasons[team.team_id] || ''} maxLength={200} placeholder="Reason, e.g. player injured"
                        onChange={(e) => setReasons(r => ({ ...r, [team.team_id]: e.target.value }))}
                        className="flex-1 h-10 px-3 rounded-lg border border-gray-200 text-sm bg-white" data-testid={`lock-reason-${team.team_id}`} />
                      <button onClick={() => lockAnyway(team.team_id)} disabled={saving || !(reasons[team.team_id] || '').trim()}
                        className="h-10 px-3 rounded-lg bg-[#2B3A67] text-white text-sm font-bold whitespace-nowrap disabled:opacity-40"
                        data-testid={`lock-anyway-${team.team_id}`}>Lock anyway</button>
                    </div>
                  ) : (
                    <p className="text-xs mt-2">Fix the squad, or ask an organiser to lock it anyway.</p>
                  )}
                </div>
              )}

              <div className="mt-3 flex gap-2">
                {locked ? (
                  <button
                    onClick={() => unlock(team.team_id)}
                    disabled={saving}
                    className="flex-1 py-2 bg-gray-100 text-gray-700 font-medium text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    data-testid={`squad-unlock-${team.team_id}`}
                  >
                    <i className="ph-bold ph-lock-open mr-1"></i>Unlock to edit
                  </button>
                ) : (
                  <button
                    onClick={() => submitAndLock(team.team_id)}
                    disabled={saving || pickedCount < minSize || pickedCount > required || !hasC}
                    className="flex-1 py-2 bg-[#3AAFA9] text-white font-bold text-sm rounded-lg hover:bg-[#2D8A85] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`squad-lock-${team.team_id}`}
                  >
                    {saving ? 'Saving...' : 'Save & Lock Squad'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {data.both_locked ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center" data-testid="both-squads-locked">
          <i className="ph-bold ph-check-circle text-emerald-600 mr-2"></i>
          <span className="text-emerald-800 text-sm font-medium">
            Both squads locked. Proceed to toss below.
          </span>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
          <i className="ph-bold ph-info text-amber-600 mr-2"></i>
          <span className="text-amber-800 text-sm">
            Lock both squads to enable the toss.
          </span>
        </div>
      )}
    </div>
  );
}
